import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, CalendarDays, Loader2, PackageSearch, RotateCcw, Search, ShoppingBag, Wallet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useBranch } from "@/context/branch";
import { Button } from "@/components/ui/button";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type ProductSalesRow = {
  productId: number;
  productName: string;
  sku: string;
  quantitySold: number;
  grossQuantitySold: number;
  returnedQuantity: number;
  exchangedQuantity: number;
  refundedQuantity: number;
  storeCreditQuantity: number;
  netQuantitySold: number;
  revenue: number;
  grossRevenue: number;
  refundedRevenue: number;
  storeCreditRevenue: number;
  netRevenue: number;
  transactions: number;
};

type ProductSalesReportResponse = {
  rows: ProductSalesRow[];
  page: number;
  pageSize: number;
  total: number;
  totals: {
    grossQuantity: number;
    returnedQuantity: number;
    exchangedQuantity: number;
    refundedQuantity: number;
    storeCreditQuantity: number;
    netQuantity: number;
    grossRevenue: number;
    refundedRevenue: number;
    storeCreditRevenue: number;
    netRevenue: number;
    products: number;
  };
  unallocatedReturnCount: number;
  unallocatedRefundRevenue: number;
  legacyRevenueLineCount: number;
};

function localDateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function currency(value: number) {
  return `₦${value.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ProductSalesReport() {
  const today = new Date();
  const [startDate, setStartDate] = useState(localDateString(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [endDate, setEndDate] = useState(localDateString(today));
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { activeBranchId, activeBranch } = useBranch();
  useEffect(() => setPage(1), [startDate, endDate, activeBranchId, search]);

  const { data, isLoading, error } = useQuery<ProductSalesReportResponse>({
    queryKey: ["product-sales-report", startDate, endDate, activeBranchId, search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      if (activeBranchId !== null) params.set("branchId", String(activeBranchId));
      if (search.trim()) params.set("search", search.trim());
      params.set("page", String(page));
      params.set("pageSize", "200");
      const response = await fetch(`${BASE}/api/reports/product-sales?${params}`, { credentials: "include" });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error ?? "Failed to load product sales report");
      return body;
    },
    enabled: Boolean(startDate && endDate),
  });

  const rows = data?.rows ?? [];
  const filtered = rows;
  const totals = data?.totals ?? { grossQuantity: 0, returnedQuantity: 0, exchangedQuantity: 0, refundedQuantity: 0, storeCreditQuantity: 0, netQuantity: 0, grossRevenue: 0, refundedRevenue: 0, storeCreditRevenue: 0, netRevenue: 0, products: 0 };
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / 200));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight"><BarChart3 className="h-7 w-7 text-primary" /> Product Sales Report</h1>
        <p className="mt-1 text-muted-foreground">Compare completed sales with refunds, store credit and exchanges.</p>
      </div>

      <Card>
        <CardContent className="grid gap-4 p-4 md:grid-cols-[1fr_180px_180px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search product name or SKU..." className="pl-9" />
          </div>
          <label className="space-y-1 text-xs font-medium text-muted-foreground">
            From
            <Input type="date" value={startDate} max={endDate} onChange={event => setStartDate(event.target.value)} className="text-foreground" />
          </label>
          <label className="space-y-1 text-xs font-medium text-muted-foreground">
            To
            <Input type="date" value={endDate} min={startDate} max={localDateString(today)} onChange={event => setEndDate(event.target.value)} className="text-foreground" />
          </label>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Net units sold</CardTitle><ShoppingBag className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><p className="text-2xl font-bold">{totals.netQuantity.toLocaleString()}</p><p className="mt-1 text-xs text-muted-foreground">{totals.grossQuantity.toLocaleString()} gross − {totals.returnedQuantity.toLocaleString()} returned</p></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Returned units</CardTitle><RotateCcw className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><p className="text-2xl font-bold">{totals.returnedQuantity.toLocaleString()}</p><p className="mt-1 text-xs text-muted-foreground">{totals.exchangedQuantity.toLocaleString()} exchanged · {totals.refundedQuantity.toLocaleString()} refunded · {totals.storeCreditQuantity.toLocaleString()} credited</p></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Net revenue</CardTitle><Wallet className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><p className="text-2xl font-bold">{currency(totals.netRevenue)}</p><p className="mt-1 text-xs text-muted-foreground">{currency(totals.grossRevenue)} gross</p></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Refunds</CardTitle><RotateCcw className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><p className="text-2xl font-bold">{currency(totals.refundedRevenue)}</p><p className="mt-1 text-xs text-muted-foreground">Cash-back returns</p></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Store credit</CardTitle><Wallet className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><p className="text-2xl font-bold">{currency(totals.storeCreditRevenue)}</p><p className="mt-1 text-xs text-muted-foreground">Returns settled as store credit</p></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Products sold</CardTitle><PackageSearch className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><p className="text-2xl font-bold">{totals.products.toLocaleString()}</p></CardContent></Card>
      </div>

      {((data?.unallocatedReturnCount ?? 0) > 0 || (data?.legacyRevenueLineCount ?? 0) > 0) && (
        <Alert>
          <RotateCcw className="h-4 w-4" />
          <AlertTitle>Some historical returns need review</AlertTitle>
          <AlertDescription>
            {(data?.unallocatedReturnCount ?? 0) > 0 && `${data!.unallocatedReturnCount.toLocaleString()} older return ${data!.unallocatedReturnCount === 1 ? "record could" : "records could"} not be fully assigned to products.`}
            {data!.unallocatedRefundRevenue > 0 && ` ${currency(data!.unallocatedRefundRevenue)} in refunds is excluded from product net revenue.`}
            {(data?.legacyRevenueLineCount ?? 0) > 0 && ` ${data!.legacyRevenueLineCount.toLocaleString()} older return line(s) use legacy revenue allocations and may not reflect sale discounts.`}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-2 text-sm text-muted-foreground"><CalendarDays className="h-4 w-4" /> {activeBranch ? activeBranch.name : "All locations"} · Completed sales and processed returns</div>

      <div className="overflow-x-auto rounded-xl border bg-background">
        <Table>
          <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>SKU</TableHead><TableHead className="text-right">Gross sold</TableHead><TableHead className="text-right">Exchanged</TableHead><TableHead className="text-right">Refunded</TableHead><TableHead className="text-right">Store credit</TableHead><TableHead className="text-right">Net sold</TableHead><TableHead className="text-right">Transactions</TableHead><TableHead className="text-right">Gross sales</TableHead><TableHead className="text-right">Refund amount</TableHead><TableHead className="text-right">Store credit amount</TableHead><TableHead className="text-right">Net revenue</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={12} className="h-40 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></TableCell></TableRow>
            ) : error ? (
              <TableRow><TableCell colSpan={12} className="h-40 text-center text-destructive">{error instanceof Error ? error.message : "Failed to load report"}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={12} className="h-48 text-center"><PackageSearch className="mx-auto mb-2 h-9 w-9 text-muted-foreground" /><p className="font-medium">No product sales found</p><p className="text-sm text-muted-foreground">Try another date range or product search.</p></TableCell></TableRow>
            ) : filtered.map(row => (
              <TableRow key={row.productId}>
                <TableCell className="font-medium">{row.productName}</TableCell>
                <TableCell className="text-muted-foreground">{row.sku || "—"}</TableCell>
                <TableCell className="text-right">{row.grossQuantitySold.toLocaleString()}</TableCell>
                <TableCell className="text-right text-muted-foreground">{row.exchangedQuantity.toLocaleString()}</TableCell>
                <TableCell className="text-right text-muted-foreground">{row.refundedQuantity.toLocaleString()}</TableCell>
                <TableCell className="text-right text-muted-foreground">{row.storeCreditQuantity.toLocaleString()}</TableCell>
                <TableCell className="text-right text-lg font-bold">{row.netQuantitySold.toLocaleString()}</TableCell>
                <TableCell className="text-right">{row.transactions.toLocaleString()}</TableCell>
                <TableCell className="text-right">{currency(row.grossRevenue)}</TableCell>
                <TableCell className="text-right text-muted-foreground">{currency(row.refundedRevenue)}</TableCell>
                <TableCell className="text-right text-muted-foreground">{currency(row.storeCreditRevenue)}</TableCell>
                <TableCell className="text-right font-semibold">{currency(row.netRevenue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && data.total > 200 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {(page - 1) * 200 + 1}–{Math.min(page * 200, data.total)} of {data.total.toLocaleString()} products</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Previous</Button>
            <span className="px-2 py-1">Page {page} of {pageCount}</span>
            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(value => value + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}